import {
  Badge,
  Card,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  GitCompare,
  List,
  Ruler,
} from 'lucide-react';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { fetchBodyImageSessionSummary } from '../../../services/bodyImageSessionManager';
import {
  fetchPatientById,
  fetchPatientMeasurementsHistory,
} from '../../../features/patients/services/patientsService';
import {
  formatAgeValue,
  formatBiologicalSex,
  formatBirthYearValue,
  formatDateTimeValue,
  formatMeasurementField,
  formatMeasurementValue,
} from '../../../features/patients/utils/formatters';
import { deriveAgeFromDateOfBirth } from '../../../features/patients/utils/nutrition';
import { resolvePatientBaseProfile } from '../../../features/patients/utils/profile';
import { BodyImageComparisonAccordionList } from '../../../features/body-image/BodyImageComparisonAccordionList';
import { formatBodyImageEventLabel } from '../../../features/body-image/eventMap';
import {
  BODY_IMAGE_METRIC_DEFINITIONS,
  findPatientMeasurementAtOrBefore,
  formatBodyImageMetricValue,
  resolveBodyImageSessionBaselineMeasurements,
} from '../../../features/body-image/utils';
import { SessionInfoCard } from '../../SessionDetails/_components/SessionInfoCard/SessionInfoCard';

interface BodyImageCompletedSessionViewProps {
  session: DeviceConfigurationApi;
}

function CompletedSessionSkeleton() {
  return (
    <Grid gutter='md'>
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap='md'>
          <Skeleton h={180} radius='md' />
          <Skeleton h={120} radius='md' />
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Skeleton h={420} radius='md' />
      </Grid.Col>
    </Grid>
  );
}

export function BodyImageCompletedSessionView({
  session,
}: BodyImageCompletedSessionViewProps) {
  const summaryQuery = useQuery({
    queryKey: ['body-image', 'summary', session.id],
    queryFn: () => fetchBodyImageSessionSummary(session.id),
  });

  const patientQuery = useQuery({
    queryKey: ['body-image', 'completed-patient', session.patient_id],
    queryFn: () => fetchPatientById(session.patient_id),
    enabled: session.patient_id > 0,
  });

  const measurementsQuery = useQuery({
    queryKey: ['body-image', 'completed-measurements', session.patient_id],
    queryFn: () => fetchPatientMeasurementsHistory(session.patient_id),
    enabled: session.patient_id > 0,
  });

  const startEvent = useMemo(
    () =>
      summaryQuery.data?.trackingEvents.find(
        event => event.eventName === 'start_bodyimage'
      ) ?? null,
    [summaryQuery.data?.trackingEvents]
  );

  const sessionMeasurementRecord = useMemo(
    () =>
      findPatientMeasurementAtOrBefore(
        measurementsQuery.data ?? [],
        session.started_at ?? session.created_at
      ),
    [measurementsQuery.data, session.created_at, session.started_at]
  );

  const registeredProfile = useMemo(
    () =>
      resolvePatientBaseProfile({
        biologicalSex:
          patientQuery.data?.profile?.biologicalSex ??
          sessionMeasurementRecord?.biologicalSex ??
          null,
        dateOfBirth:
          patientQuery.data?.profile?.dateOfBirth ??
          sessionMeasurementRecord?.dateOfBirth ??
          null,
        heightCm:
          patientQuery.data?.profile?.heightCm ??
          sessionMeasurementRecord?.heightCm ??
          null,
      }),
    [patientQuery.data?.profile, sessionMeasurementRecord]
  );

  const baselineMeasurements = useMemo(
    () =>
      resolveBodyImageSessionBaselineMeasurements({
        startEventPayload: startEvent?.eventPayload ?? null,
        measurementRecord: sessionMeasurementRecord,
      }),
    [sessionMeasurementRecord, startEvent?.eventPayload]
  );

  const measurementEntries = useMemo(() => {
    if (Object.keys(baselineMeasurements).length > 0) {
      return BODY_IMAGE_METRIC_DEFINITIONS.filter(
        definition => baselineMeasurements[definition.key] != null
      ).map(definition => ({
        key: definition.key,
        label: definition.label,
        value: formatBodyImageMetricValue(
          definition.key,
          baselineMeasurements[definition.key]
        ),
      }));
    }

    return (
      sessionMeasurementRecord?.fields
        .filter(field => field.value.trim() !== '')
        .map(field => ({
          key: String(field.id ?? field.label),
          label: field.label,
          value: formatMeasurementField(field),
        })) ?? []
    );
  }, [baselineMeasurements, sessionMeasurementRecord]);

  if (
    summaryQuery.isLoading ||
    patientQuery.isLoading ||
    measurementsQuery.isLoading
  ) {
    return <CompletedSessionSkeleton />;
  }

  const summary = summaryQuery.data ?? {
    trackingEvents: [],
    comparisons: [],
    notes: [],
  };

  return (
    <Grid gutter='md'>
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap='md'>
          <SessionInfoCard
            patientCode={session.patientCode}
            patientId={session.patient_id}
            appType={session.type}
            scenario={session.scenario}
            videoName={session.video?.name}
            source={session.video?.category ?? session.video?.product_name}
          />

          <Card withBorder p='md' radius='sm'>
            <Stack gap='sm'>
              <Group justify='space-between' align='center'>
                <Text fw={600}>Stato sessione</Text>
                <Badge color='teal' variant='light'>
                  Completata
                </Badge>
              </Group>
              <Stack gap={4}>
                <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                  Avvio
                </Text>
                <Text size='sm'>
                  {formatDateTimeValue(session.started_at ?? session.created_at)}
                </Text>
              </Stack>
              <Stack gap={4}>
                <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                  Fine
                </Text>
                <Text size='sm'>
                  {formatDateTimeValue(session.ended_at)}
                </Text>
              </Stack>
              <Stack gap={4}>
                <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                  Device
                </Text>
                <Text size='sm' fw={500}>
                  {session.deviceName || session.deviceCode || '—'}
                </Text>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Tabs defaultValue='rilevazione'>
          <Tabs.List>
            <Tabs.Tab value='rilevazione' leftSection={<Ruler size={16} />}>
              Rilevazione body
            </Tabs.Tab>
            <Tabs.Tab value='tracking' leftSection={<List size={16} />}>
              Tracking eventi
            </Tabs.Tab>
            <Tabs.Tab value='comparazioni' leftSection={<GitCompare size={16} />}>
              Manichini comparazione
            </Tabs.Tab>
            <Tabs.Tab value='note' leftSection={<FileText size={16} />}>
              Note
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='rilevazione' pt='md'>
            <Stack gap='sm'>
              <Paper withBorder p='md' radius='sm'>
                <Stack gap='sm'>
                  <Text fw={600}>Dati utente</Text>
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md'>
                    <Stack gap={4}>
                      <Text size='sm' c='dimmed'>
                        Sesso biologico
                      </Text>
                      <Text fw={600}>
                        {formatBiologicalSex(registeredProfile.biologicalSex)}
                      </Text>
                    </Stack>
                    <Stack gap={4}>
                      <Text size='sm' c='dimmed'>
                        Anno di nascita
                      </Text>
                      <Text fw={600}>
                        {formatBirthYearValue(registeredProfile.dateOfBirth)}
                      </Text>
                      <Text size='sm' c='dimmed'>
                        {formatAgeValue(
                          deriveAgeFromDateOfBirth(registeredProfile.dateOfBirth)
                        )}
                      </Text>
                    </Stack>
                    <Stack gap={4}>
                      <Text size='sm' c='dimmed'>
                        Altezza
                      </Text>
                      <Text fw={600}>
                        {formatMeasurementValue(
                          registeredProfile.heightCm,
                          'cm'
                        )}
                      </Text>
                    </Stack>
                  </SimpleGrid>
                </Stack>
              </Paper>

              <Paper withBorder p='md' radius='sm'>
                <Stack gap='sm'>
                  <Stack gap={2}>
                    <Text fw={600}>Rilevazione usata all&apos;avvio</Text>
                    <Text size='sm' c='dimmed'>
                      {startEvent?.createdAt
                        ? formatDateTimeValue(startEvent.createdAt)
                        : sessionMeasurementRecord?.createdAt
                          ? formatDateTimeValue(sessionMeasurementRecord.createdAt)
                          : 'Data non disponibile'}
                    </Text>
                  </Stack>

                  {measurementEntries.length === 0 ? (
                    <Text size='sm' c='dimmed'>
                      Nessuna rilevazione disponibile per questa sessione.
                    </Text>
                  ) : (
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                      {measurementEntries.map(entry => (
                        <Paper
                          key={entry.key}
                          withBorder
                          radius='sm'
                          p='sm'
                          bg='gray.0'
                        >
                          <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                            {entry.label}
                          </Text>
                          <Text size='sm' fw={700}>
                            {entry.value}
                          </Text>
                        </Paper>
                      ))}
                    </SimpleGrid>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value='tracking' pt='md'>
            <Card withBorder p='md' radius='sm'>
              <Stack gap='sm'>
                <Text fw={600}>Tracking eventi inviati</Text>
                {summary.trackingEvents.length === 0 ? (
                  <Text size='sm' c='dimmed'>
                    Nessun evento body image tracciato.
                  </Text>
                ) : (
                  <Table withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Evento</Table.Th>
                        <Table.Th>Timestamp</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {summary.trackingEvents.map(event => (
                        <Table.Tr key={event.id}>
                          <Table.Td>
                            {formatBodyImageEventLabel(
                              event.eventName,
                              event.eventPayload
                            )}
                          </Table.Td>
                          <Table.Td>
                            {formatDateTimeValue(event.createdAt)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value='comparazioni' pt='md'>
            <Card withBorder p='md' radius='sm'>
              <Stack gap='sm'>
                <Text fw={600}>Manichini personalizzati di comparazione</Text>
                {summary.comparisons.length === 0 ? (
                  <Text size='sm' c='dimmed'>
                    Nessun manichino personalizzato registrato in questa sessione.
                  </Text>
                ) : (
                  <BodyImageComparisonAccordionList
                    comparisons={summary.comparisons}
                    showPreview
                  />
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value='note' pt='md'>
            <Card withBorder p='md' radius='sm'>
              <Stack gap='sm'>
                <Text fw={600}>Note operative</Text>
                {summary.notes.length === 0 ? (
                  <Text size='sm' c='dimmed'>
                    Nessuna nota registrata.
                  </Text>
                ) : (
                  summary.notes.map(note => (
                    <Paper key={note.id} withBorder p='sm' radius='sm'>
                      <Stack gap={4}>
                        <Text fw={600}>{note.title}</Text>
                        <Text size='sm'>{note.description}</Text>
                        <Text size='xs' c='dimmed'>
                          {formatDateTimeValue(note.createdAt)}
                        </Text>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Grid.Col>
    </Grid>
  );
}
